import { WHOLE_MODULE_NOUN } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.constants"
import type { FeatureGroupLens } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.context"
import {
  describeRowStatus,
  objectOfLens,
  reachesThroughLens,
  rowStatusOf,
  type LensOwnGrant,
  type LensRowStatus,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.utils"
import type { FeatureGroupRuleLike } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import {
  capabilityFactsOf,
  capabilityGrantOf,
  coverageOf,
  describeKinds,
  hasModuleRule,
  type LicenseFeatureGroupCapability,
  type LicenseFeatureGroupCapabilityTree,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.capability.utils"

/** The header's hint: what kind of grant the module has, and what that means for later releases. */
const RULE_HINT = "all, incl. future additions"
const PICKS_HINT = "picked one by one · no future additions"

export type CapabilityGroupStateInput = {
  capabilities: readonly LicenseFeatureGroupCapability[]
  tree: LicenseFeatureGroupCapabilityTree
  featureGroupName: string
  rules: readonly FeatureGroupRuleLike[]
  persistedRules: readonly FeatureGroupRuleLike[]
  isPicked(code: string): boolean
  wasPicked(code: string): boolean
  lens: FeatureGroupLens
  isClosed: boolean
}

export type CapabilityGroupState = {
  moduleCode: string
  root: LicenseFeatureGroupCapability | undefined
  isAllGranted: boolean
  grantedCount: number
  includedCount: number
  added: number
  removed: number
  headerState: boolean | "indeterminate"
  isHeaderChanged: boolean
  /** Nothing left to decide: the product or the licenses include it all. */
  isFixed: boolean
  rootStatus: LensRowStatus | null
  hint: string | null
  coverage: ReturnType<typeof coverageOf>
  headerFacts: { label: string; value: string }[]
  ownGrantOf(capability: LicenseFeatureGroupCapability): LensOwnGrant
}

/**
 * Everything the module card's header shows, derived once from the draft,
 * what is persisted and the lens. Pure, so the card is composition and this
 * is the arithmetic: counts, the tri-state, what Save will do, the hint and
 * the hover facts.
 */
export function capabilityGroupStateOf(
  input: CapabilityGroupStateInput,
): CapabilityGroupState {
  const {
    capabilities,
    tree,
    featureGroupName,
    rules,
    persistedRules,
    isPicked,
    wasPicked,
    lens,
    isClosed,
  } = input
  const { root } = tree
  const moduleCode = capabilities[0].ModuleCode
  const isAllGranted = hasModuleRule(rules, moduleCode)
  const wasAllGranted = hasModuleRule(persistedRules, moduleCode)

  function ownGrantOf(capability: LicenseFeatureGroupCapability): LensOwnGrant {
    const grant = capabilityGrantOf(capability, rules, isPicked)
    return grant === "module" ? "rule" : grant === "pick" ? "pick" : null
  }
  function isGranted(capability: LicenseFeatureGroupCapability) {
    return ownGrantOf(capability) !== null
  }
  function wasGranted(capability: LicenseFeatureGroupCapability) {
    return capabilityGrantOf(capability, persistedRules, wasPicked) !== null
  }
  // What reaches the people the lens is about: the customer's users, or
  // through the person lens that one person.
  function isIncluded(capability: LicenseFeatureGroupCapability) {
    return reachesThroughLens(lens, capability, isGranted(capability))
  }

  const grantedCount = capabilities.filter(isGranted).length
  const includedCount = capabilities.filter(isIncluded).length
  const added = capabilities.filter(
    (capability) => isGranted(capability) && !wasGranted(capability),
  ).length
  const removed = capabilities.filter(
    (capability) => !isGranted(capability) && wasGranted(capability),
  ).length
  const headerState =
    grantedCount === 0
      ? false
      : grantedCount === capabilities.length
        ? true
        : "indeterminate"
  const isHeaderChanged =
    isAllGranted !== wasAllGranted ||
    (!!root && isGranted(root) !== wasGranted(root))
  // Through the customer lens, a module the product or their licenses
  // already include in full, with nothing added here, has nothing left to
  // decide in an open class. A closed class keeps the checkbox: the rule
  // is what lets a later surface of the module reach them.
  const rootStatus =
    root && lens.kind === "customer"
      ? rowStatusOf(lens, root, ownGrantOf(root))
      : null
  const isFixed =
    lens.kind === "customer" &&
    !isClosed &&
    grantedCount === 0 &&
    includedCount === capabilities.length
  const madeOf = describeKinds(capabilities)
  const coverage = coverageOf(capabilities, featureGroupName)
  const headerFacts = [
    ...(root ? capabilityFactsOf(root, tree) : []),
    ...(madeOf ? [{ label: "Made of", value: madeOf }] : []),
    {
      label: "Header tick",
      value: isAllGranted
        ? "the whole module as a rule: surfaces a later release adds are included"
        : "grants the whole module as a rule, future additions included; single ticks do not",
    },
    {
      label:
        lens.kind === "customer"
          ? `Reaches ${objectOfLens(lens)}`
          : "In this package",
      value: `${lens.kind === "customer" ? includedCount : grantedCount} of ${capabilities.length}`,
    },
    ...(lens.kind === "package"
      ? coverage.map(([name, count]) => ({
          label: name,
          value: `${count} of ${capabilities.length}`,
        }))
      : []),
  ]

  function hintOf(): string | null {
    if (isAllGranted) {
      return lens.kind === "customer"
        ? `added here · ${WHOLE_MODULE_NOUN}, incl. future additions`
        : RULE_HINT
    }
    if (grantedCount > 0) {
      return lens.kind === "customer"
        ? `${grantedCount} added here · no future additions`
        : PICKS_HINT
    }
    if (rootStatus?.included && rootStatus.by === "license") {
      return `via ${rootStatus.sources}`
    }
    // A module gated as a whole that nothing carries: the header is the
    // row, so it says why the box is empty, as any row would.
    if (rootStatus && !rootStatus.included) {
      return describeRowStatus(rootStatus, WHOLE_MODULE_NOUN)
    }
    return null
  }

  return {
    moduleCode,
    root,
    isAllGranted,
    grantedCount,
    includedCount,
    added,
    removed,
    headerState,
    isHeaderChanged,
    isFixed,
    rootStatus,
    hint: hintOf(),
    coverage,
    headerFacts,
    ownGrantOf,
  }
}
